import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPlus, Check, X as XIcon, Clock, Loader2, User as UserIcon, Banknote } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { localInputToUtcIso, parseUtc } from '../lib/calendarDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '';
  // Brief Jul 27 2026 §4 — dates backend = UTC naïf : parseUtc (heure Zurich).
  const d = parseUtc(iso) || new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const DUR_LABEL = { 30: '30 min', 60: '1h', 90: '1h30', 120: '2h' };

// Brief Jul 7 2026 §4 — section "Demandes reçues" (expert) + demandes envoyées
// (apprenant, avec réponse aux contre-propositions). Rendue dans Mes sessions.
// Brief pré-beta (Task 4) — l'expert peut fixer un prix à l'acceptation quand
// les paiements Stripe sont actifs (FORCE_FREE_SESSIONS=false).
const SessionRequestsSection = ({ onChanged }) => {
  const [data, setData] = useState({ received: [], sent: [] });
  const [busy, setBusy] = useState(null);
  const [counterFor, setCounterFor] = useState(null);
  const [counterValue, setCounterValue] = useState('');
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  // Brief pré-beta (Task 4) — formulaire prix à l'acceptation.
  const [forceFree, setForceFree] = useState(true);
  const [priceFor, setPriceFor] = useState(null);
  const [priceValue, setPriceValue] = useState('0');

  const refresh = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/session-requests/me`, { withCredentials: true });
      setData({ received: d.received || [], sent: d.sent || [] });
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    axios.get(`${API}/admin/settings/force-free-sessions`, { withCredentials: true })
      .then(({ data: d }) => setForceFree(!!d?.force_free_sessions))
      .catch(() => setForceFree(true));
  }, []);

  const act = async (url, body, successMsg) => {
    setBusy(url);
    try {
      await axios.post(url, body || {}, { withCredentials: true });
      toast.success(successMsg);
      setCounterFor(null); setDeclineFor(null); setPriceFor(null);
      setCounterValue(''); setDeclineReason(''); setPriceValue('0');
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible.');
    } finally {
      setBusy(null);
    }
  };

  // Brief pré-beta (Task 4) — l'apprenant règle la session acceptée payante.
  const payRequest = async (r) => {
    setBusy(`pay-${r.request_id}`);
    try {
      const { data: d } = await axios.post(`${API}/bookings/${r.event_id}/checkout`, {}, { withCredentials: true });
      if (d?.url) window.location.href = d.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Paiement impossible.');
      setBusy(null);
    }
  };

  const receivedActionable = data.received.filter((r) => r.status === 'pending' || r.status === 'counter_proposed');
  const sentActionable = data.sent.filter((r) =>
    r.status === 'pending' || r.status === 'counter_proposed'
    || (r.status === 'accepted' && r.payment_required && r.payment_status === 'pending'));
  if (receivedActionable.length === 0 && sentActionable.length === 0) return null;

  return (
    <div className="mb-6" data-testid="session-requests-section">
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-2">
        <CalendarPlus className="h-4 w-4 text-[#7F77DD]" />
        Demandes de session ({receivedActionable.length + sentActionable.length})
      </h2>
      <div className="space-y-2">
        {/* ── Demandes reçues (côté expert) ── */}
        {receivedActionable.map((r) => (
          <div
            key={r.request_id}
            className="p-4 rounded-2xl border border-[#7F77DD]/30 bg-[#7F77DD]/5 dark:bg-[#7F77DD]/10"
            data-testid={`session-request-received-${r.request_id}`}
          >
            <div className="flex items-start gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={r.requester?.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white text-xs">
                  {(r.requester?.display_name || '?')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-semibold">{r.requester?.display_name || 'Un apprenant'}</span> souhaite une session 1-to-1
                </p>
                <p className="text-sm font-medium text-[#7F77DD] mt-0.5">{r.subject}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  <Clock className="inline h-3 w-3 mr-1" />{fmtDate(r.requested_date)} · {DUR_LABEL[r.duration_minutes] || `${r.duration_minutes} min`}
                </p>
                {r.message && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 italic">« {r.message} »</p>}
                {r.status === 'counter_proposed' ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Contre-proposition envoyée ({fmtDate(r.counter_date)}) — en attente de réponse.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => {
                          if (forceFree) {
                            act(`${API}/session-requests/${r.request_id}/accept`, {}, 'Session créée et apprenant notifié !');
                          } else {
                            setPriceFor(priceFor === r.request_id ? null : r.request_id);
                            setCounterFor(null); setDeclineFor(null);
                          }
                        }}
                        data-testid={`session-request-accept-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        {forceFree ? 'Accepter et confirmer' : 'Accepter…'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCounterFor(counterFor === r.request_id ? null : r.request_id); setDeclineFor(null); setPriceFor(null); }}
                        data-testid={`session-request-counter-btn-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-amber-500 text-amber-700 dark:text-amber-300 text-xs font-medium hover:bg-amber-50 dark:hover:bg-amber-900/20"
                      >
                        <Clock className="h-3.5 w-3.5" /> Proposer une autre date
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeclineFor(declineFor === r.request_id ? null : r.request_id); setCounterFor(null); setPriceFor(null); }}
                        data-testid={`session-request-decline-btn-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 dark:text-red-400 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <XIcon className="h-3.5 w-3.5" /> Décliner
                      </button>
                    </div>
                    {/* Brief pré-beta (Task 4) — prix à l'acceptation (mode payant) */}
                    {priceFor === r.request_id && (
                      <div className="mt-2" data-testid={`session-request-price-form-${r.request_id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={priceValue}
                              onChange={(e) => setPriceValue(e.target.value)}
                              data-testid="session-request-price-input"
                              className="w-28 pl-2 pr-11 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">CHF</span>
                          </div>
                          <button
                            type="button"
                            disabled={!!busy}
                            onClick={() => {
                              const p = Math.max(0, Number(priceValue) || 0);
                              act(
                                `${API}/session-requests/${r.request_id}/accept`,
                                { price: p },
                                p > 0
                                  ? `Session créée — ${p} CHF demandés à l'apprenant.`
                                  : 'Session créée et apprenant notifié !'
                              );
                            }}
                            data-testid="session-request-price-confirm"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" />
                            {Number(priceValue) > 0 ? `Confirmer · ${Number(priceValue)} CHF` : 'Confirmer (gratuit)'}
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                          0 = gratuit (session confirmée directement). Avec un prix, l&apos;apprenant devra régler via Stripe pour confirmer.
                        </p>
                      </div>
                    )}
                    {counterFor === r.request_id && (
                      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`session-request-counter-form-${r.request_id}`}>
                        <input
                          type="datetime-local"
                          value={counterValue}
                          onChange={(e) => setCounterValue(e.target.value)}
                          data-testid="session-request-counter-input"
                          className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          disabled={!counterValue || !!busy}
                          onClick={() => act(`${API}/session-requests/${r.request_id}/counter`, { counter_date: localInputToUtcIso(counterValue) }, 'Contre-proposition envoyée !')}
                          data-testid="session-request-counter-send"
                          className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-400 disabled:opacity-50"
                        >
                          Envoyer
                        </button>
                      </div>
                    )}
                    {declineFor === r.request_id && (
                      <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`session-request-decline-form-${r.request_id}`}>
                        <input
                          type="text"
                          maxLength={300}
                          placeholder="Motif (optionnel)"
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          data-testid="session-request-decline-reason"
                          className="flex-1 min-w-[180px] px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          disabled={!!busy}
                          onClick={() => act(`${API}/session-requests/${r.request_id}/decline`, { reason: declineReason }, 'Demande déclinée.')}
                          data-testid="session-request-decline-send"
                          className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-400 disabled:opacity-50"
                        >
                          Confirmer le refus
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* ── Demandes envoyées (côté apprenant) ── */}
        {sentActionable.map((r) => (
          <div
            key={r.request_id}
            className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            data-testid={`session-request-sent-${r.request_id}`}
          >
            <div className="flex items-start gap-3">
              <UserIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-white">
                  Ta demande à <span className="font-semibold">{r.expert?.display_name || 'un expert'}</span> · <span className="text-[#7F77DD]">{r.subject}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Souhaité : {fmtDate(r.requested_date)} · {DUR_LABEL[r.duration_minutes] || `${r.duration_minutes} min`}
                </p>
                {r.status === 'pending' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">En attente de réponse de l'expert…</p>
                )}
                {/* Brief pré-beta (Task 4) — demande acceptée payante : CTA Payer */}
                {r.status === 'accepted' && r.payment_required && r.payment_status === 'pending' && (
                  <div className="mt-2" data-testid={`session-request-payment-pending-${r.request_id}`}>
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      {r.expert?.display_name || "L'expert"} a accepté · {r.price} CHF à régler pour confirmer la session
                    </p>
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => payRequest(r)}
                      data-testid={`session-request-pay-${r.request_id}`}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
                    >
                      {busy === `pay-${r.request_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Banknote className="h-3.5 w-3.5" />}
                      Payer {r.price} CHF
                    </button>
                  </div>
                )}
                {r.status === 'counter_proposed' && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300" data-testid={`session-request-counter-info-${r.request_id}`}>
                      {r.expert?.display_name || "L'expert"} propose le {fmtDate(r.counter_date)}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => act(`${API}/session-requests/${r.request_id}/respond-counter`, { accept: true }, 'Session confirmée !')}
                        data-testid={`session-request-counter-accept-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" /> Accepter
                      </button>
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => act(`${API}/session-requests/${r.request_id}/respond-counter`, { accept: false }, 'Contre-proposition refusée.')}
                        data-testid={`session-request-counter-decline-${r.request_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 dark:text-red-400 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <XIcon className="h-3.5 w-3.5" /> Décliner
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SessionRequestsSection;
